import React from "react";
import get from "lodash/get";

import { isEditMode } from "../utils/sendMoveToMerchant";
import { getHorizontalAlignment } from "../utils/getHorizontalAlignment";
import { ComponentConfig, WidgetProps } from "./Widget/Widget";
import { sendClickToMerchant } from "../utils/sendClickToMerchant";
import { sendResizeToMerchant } from "../utils/sendResizeToMerchant";
import { validate } from "../utils/validators";
import {
  onMouseDownMove,
  onMouseOverMove,
} from "./ComponentBase/moveComponent";
import * as actions from "../actions";
import "./ComponentBase/componentBase.css";
import { onDrag } from "./ComponentBase/dragComponent";
import {
  checkForBoundariesUpdate,
  createCornerBoxes,
  removeCornerBoxes,
} from "./ComponentBase/createCornerBoxes";
import { removeRelativeBounds } from "./ComponentBase/relativeBounds";
import { ShopifyDiscountRecord } from "../reducers/reward/shopifyActiveDiscounts";

export interface ComponentBaseProps extends WidgetProps {
  // for inputs
  formErrors: {
    [key: string]: { [index: string]: any };
  };
  shouldFocus?: boolean;
  name: string;
  onFormError: typeof actions.setWidgetFormError;
  validators: string[][];
  defaultValue: string;
  onFormChange: typeof actions.setUserInput;

  // for instrumentation
  widgetEventTrack: typeof actions.widgetEventTrack;

  // primarily for copy action
  tempTextReplace?: string;
}

export interface ComponentBaseState {
  animationClass: string;
  errorMessage?: string;
  inputClientRect?: ClientRect | DOMRect;

  // for video component
  isPlaying?: boolean;
  isMuted?: boolean;

  // for emailTagInput component
  currentTag: string;
  counterClassNames: string;
  isInputVisible: boolean;
  tags: string[];

  // for emailTagInput2 component
  inputValue: string;

  // primarily for copy action
  tempTextReplace?: string;

  // track which of a number of sub-components is selected
  selectedIndex?: number;

  //  for LastShopifyReward component
  canPollForReversal: boolean;
  lastShopifyReward?: ShopifyDiscountRecord;

  // for redemption options scroll position
  scrollState?: {
    scrollNext?: boolean;
    scrollPrev?: boolean;
  };

  // for RedemptionOptions redeem failure message
  redeemErrorMessage?: string;

  qrSVG?: string;
}

export type Zone = "n" | "s" | "e" | "w" | "nw" | "sw" | "ne" | "se" | "none";

const inputComponents = [
  "TextInput",
  "TextArea",
  "Checkbox",
  "EmailTagInput",
  "EmailTagInput2",
];

export class ComponentBase<
  T extends ComponentBaseProps,
> extends React.PureComponent<T, ComponentBaseState> {
  component: HTMLElement | null;
  zone: Zone = "none";
  invisibleLayer: HTMLElement | null;

  state: ComponentBaseState = {
    animationClass: "",
    errorMessage: undefined,
    inputClientRect: undefined,

    // for video component
    isPlaying: Boolean((this.props as any).autoPlay),
    isMuted: Boolean((this.props as any).autoPlay),

    // for emailTagInput component
    currentTag: "",
    counterClassNames: "EmailTagInput-counter",
    isInputVisible: true,
    tags: [],

    // for emailTagInput2 component
    inputValue: "",

    // primarily for copy action
    tempTextReplace: undefined,

    // track which of a number of sub-components is selected
    selectedIndex: undefined,

    //  for LastShopifyReward component
    canPollForReversal: true,
    lastShopifyReward: undefined,

    // for redemption options
    scrollState: undefined,

    qrSVG: undefined,
  };

  componentDidMount() {
    const { name, onFormChange, defaultValue, horizontalAlign } = this.props;
    const componentType = get(
      this.props.config,
      `${this.props.configName}.component`,
      ""
    ) as string;
    const isInputComponent = inputComponents.includes(componentType);

    if (this.component) {
      // add event listeners to inputs for instrumentation purposes
      if (!isEditMode() && isInputComponent) {
        this.component.addEventListener("focus", this.onInputFocus);
        this.component.addEventListener("blur", this.onInputBlur);
      }
      // allow for easy horizontal alignment
      if (horizontalAlign) {
        this.component.style.left = getHorizontalAlignment(
          window.document.body.clientWidth,
          this.component.getBoundingClientRect(),
          this.props.horizontalAlign,
          this.props.config
        );
      }

      // if in edit mode we want to add an invisible layer on the currently selected
      // component so that we can do drag and drop and drag to resize
      // add a dashed line if element is hovered over. makes it easier to find component bounds
      if (isEditMode()) {
        this.component.removeEventListener("mousedown", this.nonEditMouseDown);
        this.component.addEventListener("mouseover", this.onEditHover);
        this.component.addEventListener("mouseout", this.onEditLeaveHover);
        this.component.addEventListener("mousedown", this.createInvisibleLayer);
      }

      if (!isEditMode()) {
        this.component.removeEventListener(
          "mousedown",
          this.createInvisibleLayer
        );
        this.component.addEventListener("mousedown", this.nonEditMouseDown);
      }

      // take care of input focus state
      if (this.props.shouldFocus) {
        this.component.focus();
      }
    }

    // take care of input default value
    if (defaultValue) {
      onFormChange(name, defaultValue);
    }

    // We want this field to clear on screen load in case a user shares via email
    // and then returns to the screen to share again
    if (componentType === "EmailTagInput") {
      this.onFormChange("");
    }
  }

  componentWillUnmount() {
    const isEditing = isEditMode();
    const isInputComponent = inputComponents.includes(
      get(this.props.config, `${this.props.configName}.component`, "") as string
    );

    if (this.component) {
      this.component.removeEventListener("mousedown", this.onMouseDown);

      if (isEditing) {
        this.component.removeEventListener("mouseover", this.onEditHover);
        this.component.removeEventListener("mouseout", this.onEditLeaveHover);
        this.removeInvisibleLayer();
        removeCornerBoxes();
        removeRelativeBounds();
      }

      if (!isEditing && isInputComponent) {
        this.component.removeEventListener("focus", this.onInputFocus);
        this.component.removeEventListener("blur", this.onInputBlur);
      }
    }
  }

  // tslint:disable-next-line: cyclomatic-complexity
  componentDidUpdate(prevProps: T) {
    const {
      config,
      horizontalAlign,
      editingConfigName,
      configName,
      isEditing,
      formErrors,
      name,
    } = this.props;

    // allow for easy horizontal alignment
    if (
      this.component &&
      get(prevProps, "horizontalAlign") !== horizontalAlign
    ) {
      this.component.style.left = getHorizontalAlignment(
        window.document.body.clientWidth,
        this.component.getBoundingClientRect(),
        horizontalAlign,
        config
      );
    }

    // if we are no longer editing this component then clean up the invisible layer
    if (
      prevProps.editingConfigName === configName &&
      editingConfigName !== configName
    ) {
      this.removeInvisibleLayer();
      removeCornerBoxes();
    }

    // if we switch from edit to preview mode then remove the invisible layer
    if (this.component && !isEditing && prevProps.isEditing) {
      this.removeInvisibleLayer();
      removeCornerBoxes();
      this.component.removeEventListener(
        "mousedown",
        this.createInvisibleLayer
      );
      this.component.addEventListener("mousedown", this.nonEditMouseDown);
    }

    // if we switch from preview to edit mode then add the invisible layer creator
    if (this.component && isEditing && !prevProps.isEditing) {
      this.component.removeEventListener("mousedown", this.nonEditMouseDown);
      this.component.addEventListener("mousedown", this.createInvisibleLayer);
    }

    // check if the submit button was clicked and an error was added for this field
    if (prevProps.formErrors !== formErrors && get(formErrors, name)) {
      this.validateField(false);
    }

    // check if bounds were updated in the edit panel and adjust corner boxes
    if (isEditMode() && this.component) {
      checkForBoundariesUpdate(
        prevProps.config[configName] as ComponentConfig,
        config[configName] as ComponentConfig,
        this.component
      );
    }
  }

  nonEditMouseDown = () => {
    const { onClickAnimation, tempTextReplace } = this.props;

    if (tempTextReplace) {
      this.tempTextReplaceOnClick();
    }

    if (onClickAnimation) {
      this.setState({ animationClass: onClickAnimation as string });
    }
  };

  removeInvisibleLayer = () => {
    if (this.invisibleLayer && document.body.contains(this.invisibleLayer)) {
      document.body.removeChild(this.invisibleLayer);
    }

    // remove any stragglers
    const invisibleLayers = Array.from(
      document.querySelectorAll(".fbuy-widget-invisible-layer")
    );
    if (invisibleLayers) {
      invisibleLayers.forEach((invisibleLayer) => {
        if (invisibleLayer && document.body.contains(invisibleLayer)) {
          document.body.removeChild(invisibleLayer);
        }
      });
    }
  };

  createInvisibleLayer = (e: MouseEvent) => {
    sendClickToMerchant(e as any, this.props.configName);
    removeCornerBoxes();
    if (this.component) {
      createCornerBoxes(this.component);
      const elementBounds = this.component.getBoundingClientRect();

      // we need to create this invisible layer since the onmouseup event
      // fires on the topmost element that the mouse is above. that means that a
      // onmouseup event won't fire if our element is below a component with a
      // higher z-index.
      // since our components have different z-indexes we create a temporary layer
      // with a high z-index  and call the onmouseup on this layer
      this.invisibleLayer = document.createElement("div");
      this.invisibleLayer.className = "fbuy-widget-invisible-layer";
      this.invisibleLayer.style.position = "absolute";
      this.invisibleLayer.style.width = elementBounds.width + 20 + "px";
      this.invisibleLayer.style.height = elementBounds.height + 20 + "px";
      this.invisibleLayer.style.left = elementBounds.left - 10 + "px";
      this.invisibleLayer.style.top = elementBounds.top - 10 + "px";
      this.invisibleLayer.style.zIndex = "999999999999999999";

      document.body.appendChild(this.invisibleLayer);
      this.invisibleLayer.addEventListener("mousedown", this.onMouseDown);
      this.invisibleLayer.addEventListener("mousemove", this.onMouseMove);
      this.invisibleLayer.addEventListener("mouseover", this.onMouseOver);
      this.invisibleLayer.addEventListener("dragend", this.sendNewSize);

      return this.invisibleLayer;
    }

    return null;
  };

  isActiveComponent = () => {
    return this.props.editingConfigName === this.props.configName;
  };

  onMouseOver = (e: MouseEvent) => {
    onMouseOverMove(e, this.invisibleLayer, isEditMode());
  };

  onMouseDown = (e: React.MouseEvent<HTMLElement> | any) => {
    const { configName, config } = this.props;

    if (isEditMode() && this.component) {
      onMouseDownMove(
        e,
        this.component,
        this.invisibleLayer,
        configName,
        config
      );
    }

    if (sendClickToMerchant(e, configName)) {
      return;
    }
  };

  onClick = (event: React.FormEvent<HTMLElement>) => {
    const { onClickAnimation, configName, isEditing } = this.props;

    if (onClickAnimation && !isEditing) {
      this.setState({ animationClass: onClickAnimation as string });
    }

    if (sendClickToMerchant(event, configName)) {
      return;
    }
  };

  onEditHover = (event: MouseEvent) => {
    if (this.component && !this.isActiveComponent() && isEditMode()) {
      this.component.style.outline = "1px dashed #0085FF";
    }
  };

  onEditLeaveHover = (e: MouseEvent) => {
    if (this.component && !this.isActiveComponent() && isEditMode()) {
      this.component.style.outline = "inherit";
    }
  };

  onDragStart = (e: React.DragEvent<HTMLElement>) => {
    return false;
  };

  sendNewSize = (e: MouseEvent) => {
    if (this.component && isEditMode()) {
      const newElementBounds = this.component.getBoundingClientRect();
      const newDimensions = {
        width: newElementBounds.width + "px",
        height: newElementBounds.height + "px",
        top: newElementBounds.top + "px",
        left: newElementBounds.left + "px",
      };

      sendResizeToMerchant(e, this.props.configName, newDimensions);
    }
  };

  onFormChange = (value: any) => {
    this.props.onFormChange(this.props.name, value);
  };

  onInputFocus = (e: Event) => {
    this.props.widgetEventTrack({
      type: "widget_event",
      action: "input_focus",
      inputFieldName: get(this.props.config, `${this.props.configName}.name`),
    });
  };

  onInputBlur = (e: Event) => {
    const inputValue = get(
      this.props,
      `data.${get(
        this.props.config,
        `${this.props.configName}.name`,
        "NONE_l2E"
      )}`
    );

    if (inputValue) {
      this.props.widgetEventTrack({
        type: "widget_event",
        action: "input_blur",
        inputFieldName: get(this.props, `config.${this.props.configName}.name`),
        inputValue: JSON.stringify(inputValue),
      });
    }
  };

  // used for zone dragging
  // tslint:disable-next-line: cyclomatic-complexity
  onMouseMove = (e: MouseEvent) => {
    if (this.component && isEditMode()) {
      const elementBounds = this.component.getBoundingClientRect();
      const mouseCoordsInElement = {
        x: e.pageX - elementBounds.left,
        y: e.pageY - elementBounds.top,
      };

      const isInTopLeftZone =
        mouseCoordsInElement.x < 10 && mouseCoordsInElement.y < 10;

      const isInTopRightZone =
        mouseCoordsInElement.x > elementBounds.width - 10 &&
        mouseCoordsInElement.y < 10;

      const isInBottomLeftZone =
        mouseCoordsInElement.x < 10 &&
        mouseCoordsInElement.y > elementBounds.height - 10;

      const isInBottomRightZone =
        mouseCoordsInElement.x > elementBounds.width - 10 &&
        mouseCoordsInElement.y > elementBounds.height - 10;

      const isInTopZone = mouseCoordsInElement.y < 10;
      const isInBottomZone = mouseCoordsInElement.y > elementBounds.height - 10;
      const isInLeftZone = mouseCoordsInElement.x < 10;
      const isInRightZone = mouseCoordsInElement.x > elementBounds.width - 10;

      this.zone = "none";

      if (isInTopZone) {
        this.zone = "n";
      }
      if (isInBottomZone) {
        this.zone = "s";
      }
      if (isInLeftZone) {
        this.zone = "w";
      }
      if (isInRightZone) {
        this.zone = "e";
      }
      if (isInTopLeftZone) {
        this.zone = "nw";
      }
      if (isInTopRightZone) {
        this.zone = "ne";
      }
      if (isInBottomLeftZone) {
        this.zone = "sw";
      }
      if (isInBottomRightZone) {
        this.zone = "se";
      }

      // we need to toggle the move element listeners at the same time as the
      // resize element listeners
      if (this.zone !== "none" && this.invisibleLayer) {
        this.invisibleLayer.style.cursor = `${this.zone}-resize`;
        this.invisibleLayer.draggable = true;
        this.invisibleLayer.removeEventListener("mousedown", this.onMouseDown);
        this.invisibleLayer.addEventListener("drag", this.onDragZone);
      } else if (this.invisibleLayer) {
        this.invisibleLayer.style.cursor = "move";
        this.invisibleLayer.draggable = false;
        this.invisibleLayer.addEventListener("mousedown", this.onMouseDown);
        this.invisibleLayer.removeEventListener("drag", this.onDragZone);
      }
    }
  };

  onDragZone = (e: MouseEvent) => {
    if (this.component && this.invisibleLayer) {
      onDrag(e, this.zone, this.component, this.invisibleLayer);
    }
  };

  validateField = (setFormError: boolean, userInputValue?: any) => {
    const { validators } = this.props;
    const component = get(
      this.props.config,
      `${this.props.configName}.component`
    );

    if (validators) {
      // because we need to validate multiple items for this component we split the string into an array
      if (component === "EmailTagInput" && userInputValue) {
        userInputValue = userInputValue.replace(/\s/g, "").split(",");
      }

      this.validateValue(setFormError, userInputValue);
    }
  };

  validateValue = (setFormError: boolean, value?: any) => {
    const { name, onFormError, data, validators } = this.props;

    const rawInputValue = value ? value : get(data, name);
    const inputValue =
      typeof rawInputValue === "string" ? rawInputValue.trim() : rawInputValue;
    const errorMessage = validate(validators, inputValue, data);

    if (setFormError) {
      onFormError(name, errorMessage);
    }
  };

  tempTextReplaceOnClick = () => {
    const { tempTextReplace } = this.props;

    if (tempTextReplace) {
      this.setState({ tempTextReplace });

      setTimeout(() => {
        this.setState({ tempTextReplace: undefined });
      }, 1000);
    }
  };

  resetAnimation = () => {
    this.setState({ animationClass: "" });
  };
}
